import { useMemo } from 'react'
import { Note, isCalendarNote, filenameToDate } from '../../../utils/note'
import { FilterConfig, TaskItem } from '../../../modules/filter/types'
import { extractTasksFromNote } from '../../../modules/filter/utils'
import { NoteHeaderRenderer } from './NoteHeaderRenderer'
import { UniversalItemRenderer } from './UniversalItemRenderer'
import { createFilterTaskItem } from './utils'
import { type ItemVariant } from './types'
import { ItemSeparator } from './SectionDivider'

// Extended Note type that can include pre-limited tasks
type NoteWithLimitedTasks = Note & {
  _limitedTasks?: TaskItem[]
}

type NoteItemRendererProps = {
  note: NoteWithLimitedTasks
  filterConfig: FilterConfig
  onRevealNote: (recordName: string) => void
  variant?: ItemVariant
  className?: string
}

export function NoteItemRenderer({
  note,
  filterConfig,
  onRevealNote,
  variant = 'filter',
  className,
}: NoteItemRendererProps) {
  // Extract tasks from the note content
  const tasks = useMemo(
    () => {
      // Check if this note has pre-limited tasks (from task limit logic)
      if ('_limitedTasks' in note && Array.isArray(note._limitedTasks)) {
        return note._limitedTasks
      }
      // Otherwise extract all tasks normally
      return extractTasksFromNote(note, filterConfig)
    },
    [note, filterConfig]
  )

  // Determine context date for task display
  const contextDate = useMemo(() => {
    if (isCalendarNote(note.noteType)) {
      const { date } = filenameToDate(note.filename)
      return date?.format('YYYY-MM-DD')
    }
    return
  }, [note.noteType, note.filename])

  // Handle note click
  const handleNoteClick = () => {
    if (note.recordName) {
      onRevealNote(note.recordName)
    }
  }

  // If no tasks, don't render anything
  if (tasks.length === 0) {
    // eslint-disable-next-line unicorn/no-null -- react requires null
    return null
  }

  return (
    <div className={`note-item-renderer mb-10 ${className ?? ''}`}>
      {/* Note header */}
      <div className='cursor-pointer' onClick={handleNoteClick}>
        <NoteHeaderRenderer note={note} variant={variant} />
      </div>

      {/* Tasks */}
      <div>
        {tasks.map((task, index) => {
          const renderableItem = createFilterTaskItem(task, contextDate)

          return (
            <div
              key={`${note.recordName ?? note.filename}-task-${task.lineIndex.toString()}`}
            >
              <UniversalItemRenderer
                item={renderableItem}
                variant={variant}
                showNoteHeader={false}
                showTaskToggle={true}
                className='task-item'
                contextNote={note}
              />
              {/* Add separator between tasks */}
              {index < tasks.length - 1 && <ItemSeparator className='ml-6' />}
            </div>
          )
        })}
      </div>
    </div>
  )
}
